export const ensure = (term, ...terms) =>
  terms.length > 0
    ? (input, ...args) =>
        [term, ...terms].reduce((input, term) => term(input, ...args), input)
    : term;

export const asAString =
  (...terms) =>
  (input) =>
    ensure(...terms)((input !== undefined && String(input)) || undefined);

export const asArrayOfStrings =
  (...terms) =>
  (input) =>
    ensure(...terms)(input.length === 0 ? [] : input.split(/,\s*/));

export const notEmpty = (input) =>
  input === undefined || input.length === 0 ? invalid(`can't be empty`) : input;

export const noLongerThan = (max) => (input) => {
  if (input.length > max) {
    throw `can't be longer than ${max} characters`;
  }
  return input;
};

const toInteger = (input) => {
  if (typeof input === "number") {
    return input;
  }
  if (input.includes(".")) {
    throw "must be an integer";
  }
  return Number(input);
};

export const asAInteger =
  (...terms) =>
  (input) =>
    ensure(...terms)(input !== undefined && toInteger(input));

export const equalTo = (target) => (input) => {
  if (target !== input) {
    throw `has to be "${target}"`;
  }
  return input;
};

const quoted = (input) => `"${input}"`;

export const anyOf =
  (...targets) =>
  (input) =>
    targets.includes(input)
      ? input
      : invalid(`has to be any of ${targets.map(quoted).join(", ")}`);

export const beBetween = (min, max) => (value) => {
  if (min <= value && value <= max) {
    return value;
  }
  throw `must be a number between ${min} and ${max}`;
};

export const beAtLeast = (min) => (value) => {
  if (min <= value) {
    return value;
  }
  throw `must be a at least ${min}`;
};

const tryForWorkingTermWith =
  (input) =>
  ({ failures = [], result }, term) => {
    if (result !== undefined) {
      return { result };
    }
    try {
      return { result: term(input) };
    } catch (failed) {
      return { failures: [...failures, failed] };
    }
  };

export const oneOf =
  (...terms) =>
  (input) => {
    const { result, failures = [] } = terms.reduce(
      tryForWorkingTermWith(input),
      { failures: [] }
    );
    if (result === undefined && failures.length > 0) {
      throw failures.join(" or ");
    }
    return result;
  };

const unCapitalize = (input) =>
  input.slice(0, 1).toLowerCase() + input.slice(1);

export const wrapErrors =
  (wrapError = (error) => error, payload) =>
  (...args) => {
    try {
      return payload(...args);
    } catch (error) {
      throw wrapError(unCapitalize(String(error)));
    }
  };

export const invalid = (reason) => {
  throw reason;
};

const timesFound = (char, input) =>
  input.length - input.replaceAll(char, "").length;

export const maxCharacterRepetitions =
  (char, max = 1) =>
  (input) =>
    timesFound(char, input) > max
      ? invalid(`can't have more than ${max} occurences of ${char}`)
      : input;

export const maxWildcards = wrapErrors(
  () => "can't have more than two wildcards *",
  maxCharacterRepetitions("*", 2)
);

export const itsProperty =
  (field, ...terms) =>
  (target, ...args) => {
    return {
      [field]: ensure(...terms)(target[field], ...args),
    };
  };

const whenMergeable = (input) => (typeof input === "object" ? input : {});

export const ensureProperties =
  (...terms) =>
  (entry, ...params) =>
    terms.reduce(
      (result, term) => ({
        ...result,
        ...whenMergeable(term(entry, ...params)),
      }),
      entry
    );

const itsFalse = () => false;

const anotherMatchingItem = (reference, compare=itsFalse) =>
  reference.deleted === true
    ? () => false
    : (other) =>
        other.deleted !== true &&
        other !== reference &&
        other.__id !== reference.__id &&
        compare(reference, other)

const asString = item => String(item)

const wontAllowOverlappingOf = (describe=asString) => (target, overlapping) =>
  overlapping === undefined
    ? target
    : invalid(`Can't add ${describe(target)} as it's already present`);

export const validateNotOverlapingOf = (compare, describe) =>
  (item, list) =>
    wontAllowOverlappingOf(describe)(
      item, list.find(anotherMatchingItem(item, compare))
    );
